import { useQuery } from "@tanstack/react-query";
import { AdminTopline } from "../../components/console";
import { KpiTile } from "../../components/dx";
import { Download } from "../../components/icons";
import { api, authHeaders, describeError } from "../../lib/api";
import {
  EXPENSE_CAPTION, EXPENSE_CATEGORY_LO, NO_DISTRICT, NO_PROVINCE, NO_TASAENG, REPORT_TABS, REVENUE_CAPTION,
  isEmptyReport, reportSheets, reportTable, safeFileName, summarize, type FinanceReport, type ReportCell,
  type ReportCol, type ReportTabId, type ReportTable,
} from "../../lib/finance-report";
import { num } from "../../lib/format";
import { buildXlsx, saveBlob } from "../../lib/xlsx";
import { FinanceFilters } from "./FinanceFilters";
import { useFinanceFilters, usePlaceName, type FinanceFiltersState } from "./filters";

/**
 * ລາຍງານການເງິນ — 8 รายงานจาก `GET /v1/admin/reports/finance` ครั้งเดียวต่อตัวกรอง (ADR-0034)
 *
 * รวมยอดฝั่งเบราว์เซอร์ทั้งหมด (`lib/finance-report.ts`) — ทุกแท็บจึงใช้ข้อมูลชุดเดียวกัน ยอดรวมตรงกันเสมอ
 * และไฟล์ Excel ใช้ตาราง (`ReportTable`) ตัวเดียวกับที่วาดบนจอ
 */

async function fetchReport(query: FinanceFiltersState["query"]): Promise<FinanceReport> {
  const { data, error, response } = await api.GET("/v1/admin/reports/finance", {
    params: { query },
    headers: authHeaders(),
  });
  if (error || !data) throw new Error(describeError(error, response.status));
  return data;
}

const UNKNOWN_NAMES = new Set([NO_TASAENG, NO_DISTRICT, NO_PROVINCE, "ບໍ່ລະບຸໝວດ"]);

function fmtCell(v: ReportCell, c: ReportCol | undefined): string {
  if (v === null) return "—";
  if (typeof v === "string") return v;
  if (c?.kind === "pct") return `${v.toLocaleString("lo-LA", { maximumFractionDigits: 1 })}%`;
  return num(v);
}

const isNumCol = (c: ReportCol | undefined) => c?.kind === "int" || c?.kind === "money" || c?.kind === "pct";

/** ตารางรายงานแบบกลาง: หัว + แถว + แถวรวมตัวหนา */
function ReportTableView({ t, label }: { t: ReportTable; label: string }) {
  const cls = (c: ReportCol | undefined) => (isNumCol(c) ? "num is-right" : c?.kind === "code" ? "num" : undefined);
  return (
    <div className="table-scroll">
      <table className="data-table dx-table" aria-label={label}>
        <thead>
          <tr>
            {t.cols.map((c) => (
              <th key={c.label} className={isNumCol(c) ? "is-right" : undefined}>
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {t.rows.map((r, i) => (
            <tr key={i}>
              {r.map((v, j) => (
                <td
                  key={j}
                  className={
                    typeof v === "string" && UNKNOWN_NAMES.has(v) ? "dx-faint" : cls(t.cols[j])
                  }
                >
                  {fmtCell(v, t.cols[j])}
                </td>
              ))}
            </tr>
          ))}
          <tr className="dx-table__total">
            {t.total.map((v, j) => (
              <td key={j} className={cls(t.cols[j])}>
                {fmtCell(v, t.cols[j])}
              </td>
            ))}
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function SummaryView({ rep }: { rep: FinanceReport }) {
  const s = summarize(rep);
  return (
    <>
      <div className="dx-kpis dx-kpis--3">
        <KpiTile
          label="ລາຍຮັບລວມ"
          value={num(s.revenue)}
          unit="ກີບ"
          tone="green"
          hint={`ຈາກ ${num(s.payments)} ການຊຳລະ`}
        />
        <KpiTile
          label="ລາຍຈ່າຍລວມ"
          value={num(s.expense)}
          unit="ກີບ"
          tone="gold"
          hint={`${num(s.expenseEntries)} ລາຍການ`}
        />
        <KpiTile
          label="ຍອດເຫຼືອ"
          value={num(s.balance)}
          unit="ກີບ"
          tone={s.balance < 0 ? "red" : "navy"}
          hint="ລາຍຮັບລວມ − ລາຍຈ່າຍລວມ"
        />
      </div>

      <div className="dx-two fin-gap">
        <section className="fin-section">
          <h2 className="section-title">ລາຍຮັບ</h2>
          <ReportTableView
            label="ລາຍຮັບ"
            t={{
              cols: [
                { label: "ລາຍການ", kind: "text" },
                { label: "ຈຳນວນເງິນ (ກີບ)", kind: "money" },
              ],
              rows: [
                ["ຄ່າທຳນຽມ", s.fee],
                ["ອາກອນ", s.stamp],
              ],
              total: ["ລວມ", s.revenue],
            }}
          />
          <p className="dx-cap fin-cap">{REVENUE_CAPTION}</p>
        </section>
        <section className="fin-section">
          <h2 className="section-title">ລາຍຈ່າຍ</h2>
          <ReportTableView
            label="ລາຍຈ່າຍ"
            t={{
              cols: [
                { label: "ໝວດ", kind: "text" },
                { label: "ຈຳນວນລາຍການ", kind: "int" },
                { label: "ຈຳນວນເງິນ (ກີບ)", kind: "money" },
              ],
              rows: s.expenseByCategory.map((c) => [EXPENSE_CATEGORY_LO[c.category], c.entries, c.amount]),
              total: ["ລວມ", s.expenseEntries, s.expense],
            }}
          />
          <p className="dx-cap fin-cap">{EXPENSE_CAPTION}</p>
        </section>
      </div>
    </>
  );
}

/** แท็บที่มีคอลัมน์รายจ่าย — ต้องมีป้ายที่มาของรายจ่ายใต้ตาราง */
const WITH_EXPENSE: readonly ReportTabId[] = ["tasaeng", "district", "province"];

export function FinanceReportPage() {
  const f = useFinanceFilters();
  const placeName = usePlaceName(f.place);
  const tabParam = f.params.get("tab");
  const tab: ReportTabId = REPORT_TABS.some((t) => t.id === tabParam) ? (tabParam as ReportTabId) : "summary";
  const tabInfo = REPORT_TABS.find((t) => t.id === tab) ?? REPORT_TABS[0];

  const report = useQuery({
    queryKey: ["finance", "report", f.key],
    queryFn: () => fetchReport(f.query),
  });
  const rep = report.data;

  const fileBase = safeFileName(`${f.days.from}_${f.days.to}_${placeName}`);
  function exportTab() {
    if (!rep) return;
    saveBlob(buildXlsx(reportSheets(rep, [tab])), `${safeFileName(`ລາຍງານການເງິນ_${tabInfo.sheet}`)}_${fileBase}.xlsx`);
  }
  function exportAll() {
    if (!rep) return;
    saveBlob(buildXlsx(reportSheets(rep, REPORT_TABS.map((t) => t.id))), `ລາຍງານການເງິນ_ທັງໝົດ_${fileBase}.xlsx`);
  }

  return (
    <div>
      <AdminTopline
        title="ລາຍງານການເງິນ"
        subtitle={`${placeName} · ${f.periodText}`}
        actions={
          <>
            <button type="button" className="btn-secondary btn-sm" disabled={!rep} onClick={exportTab}>
              <Download size={14} /> ສົ່ງອອກ Excel
            </button>
            <button type="button" className="btn-secondary btn-sm" disabled={!rep} onClick={exportAll}>
              <Download size={14} /> ສົ່ງອອກທັງໝົດ
            </button>
          </>
        }
      />

      <FinanceFilters f={f} />

      <div className="group-chips" role="tablist" aria-label="ລາຍງານ">
        {REPORT_TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            className={tab === t.id ? "group-chip is-active" : "group-chip"}
            onClick={() => f.go({ extra: { tab: t.id === "summary" ? null : t.id } })}
          >
            {t.label}
          </button>
        ))}
      </div>

      {report.error ? (
        <div className="alert-error">ໂຫຼດລາຍງານບໍ່ສຳເລັດ: {report.error.message}</div>
      ) : !rep ? (
        <p className="dx-empty">ກຳລັງໂຫຼດ…</p>
      ) : isEmptyReport(rep) ? (
        <p className="dx-empty">ບໍ່ມີລາຍຮັບ/ລາຍຈ່າຍໃນຊ່ວງນີ້</p>
      ) : tab === "summary" ? (
        <SummaryView rep={rep} />
      ) : (
        <section className="fin-section">
          <h2 className="section-title">{tabInfo.label}</h2>
          <ReportTableView t={reportTable(rep, tab)} label={tabInfo.label} />
          <p className="dx-cap fin-cap">{REVENUE_CAPTION}</p>
          {WITH_EXPENSE.includes(tab) ? <p className="dx-cap fin-cap">{EXPENSE_CAPTION}</p> : null}
        </section>
      )}
    </div>
  );
}
